import { Box, Chip, Paper, Stack, Text } from '@ltvco/refresh-lib/theme';

import { constants } from 'appConstants';

const INSUFFICIENT_ELEMENTS_TITLE = 'Insufficient elements';

const chipSx = {
  height: 24,
  fontSize: '0.68rem',
  fontWeight: 600,
  '& .MuiChip-label': { px: 1 },
} as const;

export type DeviceCardProps = {
  manufacturer?: string;
  marketingname?: string;
  modelname?: string;
  operatingsys?: string;
  devicetype?: string;
  nfc?: string | boolean;
  bluetooth?: string | boolean;
  wlan?: string | boolean;
};

function formatLabelBeforeSlash(value: string): string {
  const slashIndex = value.indexOf('/');
  const base = slashIndex === -1 ? value : value.slice(0, slashIndex);
  return base.trim();
}

function isConnectivityEnabled(value: string | boolean | undefined): boolean {
  if (typeof value === 'boolean') {
    return value;
  }
  return String(value ?? '').toLowerCase() === 'yes';
}

function isMissingOrNa(value: string | boolean | undefined): boolean {
  if (typeof value === 'boolean') {
    return !value;
  }
  const normalized = String(value ?? '')
    .trim()
    .toLowerCase();
  return !normalized || normalized === 'n/a' || normalized === 'na';
}

function hasInsufficientDeviceData(props: DeviceCardProps): boolean {
  return (
    isMissingOrNa(props.manufacturer) &&
    isMissingOrNa(props.marketingname) &&
    isMissingOrNa(props.modelname) &&
    isMissingOrNa(props.operatingsys) &&
    isMissingOrNa(props.devicetype)
  );
}

export default function DeviceCard({
  manufacturer = '',
  marketingname = '',
  modelname = '',
  operatingsys = '',
  devicetype = '',
  nfc,
  bluetooth,
  wlan,
}: DeviceCardProps) {
  const { images } = constants;
  const insufficientData = hasInsufficientDeviceData({
    manufacturer,
    marketingname,
    modelname,
    operatingsys,
    devicetype,
    nfc,
    bluetooth,
    wlan,
  });

  const deviceTypeLabel = formatLabelBeforeSlash(devicetype || 'Device');
  const subtitle = [manufacturer, modelname]
    .filter((part) => part && !isMissingOrNa(part))
    .join(' · ');

  const connectivity = [
    { label: 'NFC', enabled: isConnectivityEnabled(nfc) },
    { label: 'Bluetooth', enabled: isConnectivityEnabled(bluetooth) },
    { label: 'WLAN', enabled: isConnectivityEnabled(wlan) },
  ].filter((item) => item.enabled);

  return (
    <Paper
      id="device-fraud-device-section"
      elevation={0}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2.5,
        mt: 2,
        p: { xs: 2, sm: 2.5 },
        border: 1,
        borderColor: 'divider',
        borderRadius: 2.5,
        bgcolor: 'background.paper',
        maxWidth: 800,
        width: '100%',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: 90,
          height: 90,
          borderRadius: 1.25,
          bgcolor: 'primary.light',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <img
          src={images.imgDevice}
          alt=""
          width={56}
          height={56}
          style={{ objectFit: 'contain' }}
        />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Text
          variant="subtitle1"
          fontWeight={600}
          noWrap
          color={insufficientData ? 'text.secondary' : 'text.primary'}
          sx={{ lineHeight: 1.3, mb: insufficientData ? 0 : 0.3 }}
        >
          {insufficientData ? INSUFFICIENT_ELEMENTS_TITLE : marketingname}
        </Text>

        {!insufficientData && subtitle && (
          <Text
            variant="caption"
            color="text.secondary"
            noWrap
            sx={{ display: 'block', mb: 1, fontSize: '0.78rem' }}
          >
            {subtitle}
          </Text>
        )}

        {!insufficientData && (
          <Stack direction="row" flexWrap="wrap" gap={0.75}>
            {deviceTypeLabel && !isMissingOrNa(devicetype) && (
              <Chip
                label={deviceTypeLabel.toUpperCase()}
                size="small"
                sx={{
                  ...chipSx,
                  letterSpacing: '0.04em',
                  bgcolor: 'primary.light',
                  color: 'primary.dark',
                  border: 1,
                  borderColor: 'primary.main',
                }}
              />
            )}

            {operatingsys && !isMissingOrNa(operatingsys) && (
              <Chip
                label={operatingsys}
                size="small"
                sx={{
                  ...chipSx,
                  fontWeight: 500,
                  bgcolor: 'background.default',
                  color: 'text.secondary',
                }}
              />
            )}

            {connectivity.map((item) => (
              <Chip
                key={item.label}
                label={item.label}
                size="small"
                sx={{
                  ...chipSx,
                  fontWeight: 500,
                  bgcolor: 'success.light',
                  color: 'success.dark',
                }}
              />
            ))}
          </Stack>
        )}
      </Box>
    </Paper>
  );
}
